import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlayerProvider } from "@/context/PlayerContext";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import Inicio from "@/pages/Inicio";
import Canciones from "@/pages/Canciones";
import Beats from "@/pages/Beats";
import Proyectos from "@/pages/Proyectos";
import Mas from "@/pages/Mas";
import api from "@/lib/api";
import { applyForRoute, rememberHqAccent } from "@/lib/theme";

const pageLoaders = {
  Register: () => import("@/pages/Register"),
  Recover: () => import("@/pages/Recover"),
  SongWorkspace: () => import("@/pages/SongWorkspace"),
  ProjectWorkspace: () => import("@/pages/ProjectWorkspace"),
  Estadisticas: () => import("@/pages/Estadisticas"),
  Archivo: () => import("@/pages/Archivo"),
  Cuenta: () => import("@/pages/Cuenta"),
  Configuracion: () => import("@/pages/Configuracion"),
  Releases: () => import("@/pages/Releases"),
  PreviewsManager: () => import("@/pages/PreviewsManager"),
  PublicPreviews: () => import("@/pages/PublicPreviews"),
  PostingCalendar: () => import("@/pages/PostingCalendar"),
  Catalog: () => import("@/pages/Catalog"),
};
const Catalog = lazy(pageLoaders.Catalog);
const Register = lazy(pageLoaders.Register);
const Recover = lazy(pageLoaders.Recover);
const SongWorkspace = lazy(pageLoaders.SongWorkspace);
const ProjectWorkspace = lazy(pageLoaders.ProjectWorkspace);
const Estadisticas = lazy(pageLoaders.Estadisticas);
const Archivo = lazy(pageLoaders.Archivo);
const Cuenta = lazy(pageLoaders.Cuenta);
const Configuracion = lazy(pageLoaders.Configuracion);
const Releases = lazy(pageLoaders.Releases);
const PreviewsManager = lazy(pageLoaders.PreviewsManager);
const PublicPreviews = lazy(pageLoaders.PublicPreviews);
const PostingCalendar = lazy(pageLoaders.PostingCalendar);

const blank = <div className="min-h-[100dvh] bg-[#0A0A0C]" />;
const pageFallback = <div className="text-center text-zinc-500 text-sm py-10">Loading…</div>;

// Persistent HQ shell: Layout (nav, sidebar, player) stays mounted across tab switches.
function PrivateShell() {
  const { user, loading } = useAuth();
  useEffect(() => {
    if (!user) return undefined;
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
    const h = idle(() => Object.values(pageLoaders).forEach((load) => load().catch(() => {})));
    return () => (window.cancelIdleCallback || clearTimeout)(h);
  }, [user]);
  if (loading) return <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center text-zinc-500 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout><Suspense fallback={pageFallback}><Outlet /></Suspense></Layout>;
}

// Public wrapper: no auth, no layout, no /auth/me
function Public({ children }) {
  return <>{children}</>;
}

// Public artist domain: root path renders LXN WRLD without login. Any other host keeps LXN HQ private.
// Match "lxnwrld.com" and any subdomain like "www.lxnwrld.com".
const PUBLIC_ARTIST_HOST_RE = /(^|\.)lxnwrld\.com$/i;

// Keeps HQ (red) and public LXNWRLD (purple) theming separate: only HQ routes get html.hq-theme.
export function isPublicPath(pathname) {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  return pathname.startsWith("/p/") || (PUBLIC_ARTIST_HOST_RE.test(host) && pathname === "/");
}
function ThemeScope() {
  const { pathname } = useLocation();
  useEffect(() => {
    const pub = isPublicPath(pathname);
    document.documentElement.classList.toggle("hq-theme", !pub);
    applyForRoute(pub);
  }, [pathname]);
  useEffect(() => {
    if (!localStorage.getItem("lxn_token") || isPublicPath(window.location.pathname)) return;
    api.get("/theme").then(({ data }) => { rememberHqAccent(data.hq_accent); applyForRoute(isPublicPath(window.location.pathname)); }).catch(() => {});
  }, []);
  return null;
}

function App() {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isPublicRoot = PUBLIC_ARTIST_HOST_RE.test(host);
  return (
    <BrowserRouter>
      <ThemeScope />
      <AuthProvider>
        <PlayerProvider>
          <Suspense fallback={blank}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/recover" element={<Recover />} />
            {/* Public preview routes — no Layout wrapper, no auth */}
            <Route path="/p/:wsSlug" element={<PublicPreviews />} />
            <Route path="/p/:wsSlug/:slug" element={<PublicPreviews />} />
            <Route path="/previews" element={<Navigate to="/wrld" replace />} />
            {/* Spanish route aliases for old PWA installs */}
            <Route path="/registro" element={<Navigate to="/register" replace />} />
            <Route path="/recuperar" element={<Navigate to="/recover" replace />} />
            {isPublicRoot && <Route path="/" element={<PublicPreviews />} />}
            <Route path="/canciones" element={<Navigate to="/songs" replace />} />
            <Route path="/canciones/:id" element={<CanRedirect />} />
            <Route path="/proyectos" element={<Navigate to="/projects" replace />} />
            <Route path="/proyectos/:id" element={<ProjRedirect />} />
            <Route path="/estadisticas" element={<Navigate to="/statistics" replace />} />
            <Route path="/archivo" element={<Navigate to="/archive" replace />} />
            <Route path="/cuenta" element={<Navigate to="/account" replace />} />
            <Route path="/configuracion" element={<Navigate to="/settings" replace />} />
            <Route path="/mas" element={<Navigate to="/more" replace />} />
            <Route element={<PrivateShell />}>
              <Route path="/wrld" element={<PreviewsManager />} />
              <Route path="/wrld/calendar" element={<PostingCalendar />} />
              {!isPublicRoot && <Route path="/" element={<Inicio />} />}
              <Route path="/songs" element={<Canciones />} />
              <Route path="/songs/:id" element={<SongWorkspace />} />
              <Route path="/beats" element={<Beats />} />
              <Route path="/projects" element={<Proyectos />} />
              <Route path="/projects/:id" element={<ProjectWorkspace />} />
              <Route path="/releases" element={<Releases />} />
              <Route path="/statistics" element={<Estadisticas />} />
              <Route path="/archive" element={<Archivo />} />
              <Route path="/account" element={<Cuenta />} />
              <Route path="/settings" element={<Configuracion />} />
              <Route path="/more" element={<Mas />} />
              <Route path="/catalog" element={<Catalog />} />
              <Route path="/notes" element={<Navigate to="/songs" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          <Toaster position="top-center" theme="dark" />
        </PlayerProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function CanRedirect() {
  const id = window.location.pathname.split("/").pop();
  return <Navigate to={`/songs/${id}`} replace />;
}
function ProjRedirect() {
  const id = window.location.pathname.split("/").pop();
  return <Navigate to={`/projects/${id}`} replace />;
}

export default App;
